import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { CompactStepper } from '@/components/ui/Stepper';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { Switch } from '@/components/ui/switch';
import {
  Loader2,
  ChevronDown,
  Link2,
  FileText,
  Package,
  Plus,
  Trash2,
  Upload,
  ExternalLink,
  Wrench,
  BookOpenCheck,
  Scissors,
  Truck,
  Image,
  Search,
  Layers,
} from 'lucide-react';
import { PlybookFormDialog } from '@/components/Plybook/PlybookFormDialog';

interface AssemblyMaterial {
  id?: string;
  material_id: string | null;
  material_name: string;
  quantity: number;
  unit: string;
  notes: string;
}

interface Attachment {
  id?: string;
  file_name: string;
  file_url: string;
  file_type: string;
  version: string;
  description: string;
}

interface WorkshopShelf {
  id: string;
  code: string;
  name: string;
  min_skill_level: 'A' | 'B' | 'C';
  shelf_type: 'standard' | 'paint' | 'composite';
}

interface ProductFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product?: any;
  onSuccess?: () => void;
  pendingData?: any; // Datos recuperados de localStorage al volver de crear plybook
}

export function ProductFormDialog({ open, onOpenChange, product, onSuccess, pendingData }: ProductFormDialogProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [molds, setMolds] = useState<any[]>([]);
  const [plybooks, setPlybooks] = useState<any[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [showPlybookDialog, setShowPlybookDialog] = useState(false);

  // Guardar estado del producto y navegar a crear plybook
  const handleCreatePlybookInModule = () => {
    const formValues = form.getValues();
    const pendingProduct = {
      formData: formValues,
      attachments,
      assemblyMaterials,
      expeditionMaterials,
      timestamp: Date.now(),
    };
    localStorage.setItem('pending_product_model', JSON.stringify(pendingProduct));
    
    // Navegar al módulo de plybooks
    const params = new URLSearchParams();
    params.set('from', 'product');
    if (formValues.customer_id) params.set('customer_id', formValues.customer_id);
    if (formValues.project_id) params.set('project_id', formValues.project_id);
    
    onOpenChange(false); // Cerrar el diálogo
    navigate('/plybooks?' + params.toString());
  };
  const [nextNumber, setNextNumber] = useState<number | null>(null);

  // Sections state
  const [urlsOpen, setUrlsOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [expeditionOpen, setExpeditionOpen] = useState(false);

  // Assembly materials
  const [assemblyMaterials, setAssemblyMaterials] = useState<AssemblyMaterial[]>([]);
  const [colaminationMaterials, setColaminationMaterials] = useState<AssemblyMaterial[]>([]);
  const [colamOpen, setColamOpen] = useState(false);

  // Expedition materials (mismo patrón que assembly)
  const [expeditionMaterials, setExpeditionMaterials] = useState<AssemblyMaterial[]>([]);
  const [materialSearch, setMaterialSearch] = useState("");

  // Estanterías de corte taller
  const [shelves, setShelves] = useState<WorkshopShelf[]>([]);

  // Attachments
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Create Design WO dialog
  const [showCreateWODialog, setShowCreateWODialog] = useState(false);
  const [creatingWO, setCreatingWO] = useState(false);

  const form = useForm({
    defaultValues: {
      name: '',
      reference: '',
      version: 'V1',
      description: '',
      customer_id: '',
      project_id: '',
      mold_id: '',
      plybook_id: '',
      document_url: '',
      manual_url: '',
      client_document_url: '',
      client_manual_url: '',
      client_specs_url: '',
      client_portal_url: '',
      finish_type: '',
      piece_complexity: '',
      requires_paint: false,
      cutting_skill_level: '',
      cutting_shelf_id: '',
      composite_shelf_id: '',
      cutting_max_minutes: '',
    },
  });

  useEffect(() => {
    if (open) {
      fetchCustomers();
      fetchProjects();
      fetchMolds();
      fetchPlybooks();
      fetchMaterials();
      fetchShelves();

      if (product) {
        form.reset({
          name: product.name || '',
          reference: product.reference || '',
          version: product.version || 'V1',
          description: product.description || '',
          customer_id: product.customer_id || '',
          project_id: product.project_id || '',
          mold_id: product.mold_id || '',
          plybook_id: product.plybook_id || '',
          document_url: product.document_url || '',
          manual_url: product.manual_url || '',
          client_document_url: product.client_document_url || '',
          client_manual_url: product.client_manual_url || '',
          client_specs_url: product.client_specs_url || '',
          client_portal_url: product.client_portal_url || '',
          finish_type: product.finish_type || '',
          piece_complexity: product.piece_complexity || '',
          requires_paint: product.requires_paint ?? false,
          cutting_skill_level: product.cutting_skill_level || '',
          cutting_shelf_id: product.cutting_shelf_id || '',
          composite_shelf_id: product.composite_shelf_id || '',
          cutting_max_minutes: product.cutting_max_minutes != null ? String(product.cutting_max_minutes) : '',
        });
        fetchAssemblyMaterials(product.id);
        fetchExpeditionMaterials(product.id);
        fetchAttachments(product.id);
      } else {
        form.reset({
          name: '',
          reference: '',
          version: 'V1',
          description: '',
          customer_id: '',
          project_id: '',
          mold_id: '',
          plybook_id: '',
          document_url: '',
          manual_url: '',
          client_document_url: '',
          client_manual_url: '',
          client_specs_url: '',
          client_portal_url: '',
          finish_type: '',
          piece_complexity: '',
          requires_paint: false,
          cutting_skill_level: '',
          cutting_shelf_id: '',
      composite_shelf_id: '',
          cutting_max_minutes: '',
        });
        setAssemblyMaterials([]);
        setColaminationMaterials([]);
        setExpeditionMaterials([]);
        setAttachments([]);

        api.from('product_models')
          .select('product_number')
          .order('product_number', { ascending: false })
          .limit(1)
          .then(({ data }) => {
            const maxNum = data?.[0]?.product_number || 0;
            setNextNumber(maxNum + 1);
          });
      }
    }
  }, [open, product, form]);

  // Restaurar datos pendientes (cuando vuelve de crear plybook)
  useEffect(() => {
    if (open && pendingData && !product) {
      form.reset(pendingData.formData);
      setAssemblyMaterials(pendingData.assemblyMaterials || []);
      setColaminationMaterials(pendingData.colaminationMaterials || []);
      setExpeditionMaterials(pendingData.expeditionMaterials || []);
      setAttachments(pendingData.attachments || []);
    }
  }, [open, pendingData, product, form]);

  const fetchCustomers = async () => {
    const { data } = await api
      .from('customers')
      .select('id,company_name,customer_number')
      .eq('is_active', true)
      .order('company_name');
    setCustomers(data || []);
  };

  const fetchProjects = async () => {
    const { data } = await api
      .from('projects')
      .select('id,name,project_number,customer_id')
      .eq('is_active', true)
      .order('project_number', { ascending: false });
    setProjects(data || []);
  };

  const fetchMolds = async () => {
    const { data } = await api
      .from('molds')
      .select('id,name,code')
      .eq('is_active', true)
      .order('name');
    setMolds(data || []);
  };

  const fetchPlybooks = async () => {
    const { data } = await api
      .from('plybooks')
      .select('id,name,code,total_plies,status,customer_id,project_id')
      .eq('is_active', true)
      .order('name');
    setPlybooks(data || []);
  };

  // Ensamblaje y Expedición usan EXCLUSIVAMENTE el catálogo de Materias Primas
  // (tabla raw_materials, módulo "Materias Primas"), no el inventario general.
  const fetchMaterials = async () => {
    const { data } = await api
      .from('raw_materials')
      .select('id,name,code,unit,category')
      .eq('is_active', true)
      .order('name');
    setMaterials(data || []);
  };

  const fetchShelves = async () => {
    const { data } = await api
      .from('workshop_shelves')
      .select('id,code,name,min_skill_level,shelf_type')
      .eq('is_active', true)
      .order('sort_order');
    setShelves(data || []);
  };

  const fetchExpeditionMaterials = async (productId: string) => {
    const { data } = await api
      .from('product_model_expedition_materials')
      .select('*, raw_material:raw_materials(id,name,unit)')
      .eq('product_model_id', productId)
      .order('sort_order');
    setExpeditionMaterials((data || []).map((m: any) => ({
      id: m.id,
      material_id: m.raw_material_id || null,
      material_name: m.raw_material?.name || m.material_name || '',
      quantity: m.quantity,
      unit: m.unit,
      notes: m.notes || '',
    })));
  };

  const fetchAssemblyMaterials = async (productId: string) => {
    const { data } = await api
      .from('product_model_assembly_materials')
      .select('*, raw_material:raw_materials(id,name,unit)')
      .eq('product_model_id', productId)
      .order('sort_order');
    setAssemblyMaterials((data || []).map((m: any) => ({
      id: m.id,
      material_id: m.raw_material_id || null,
      material_name: m.raw_material?.name || m.material_name || '',
      quantity: m.quantity,
      unit: m.unit,
      notes: m.notes || '',
    })));
  };

  const fetchAttachments = async (productId: string) => {
    const { data } = await api
      .from('product_model_attachments')
      .select('*')
      .eq('product_model_id', productId)
      .eq('is_current', true)
      .order('created_at', { ascending: false });
    setAttachments((data || []).map((a: any) => ({
      id: a.id,
      file_name: a.file_name,
      file_url: a.file_url,
      file_type: a.file_type || '',
      version: a.version || 'V1',
      description: a.description || '',
    })));
  };

  const watchCustomerId = form.watch('customer_id');
  const watchMoldId = form.watch('mold_id');
  const watchPlybookId = form.watch('plybook_id');
  const watchName = form.watch('name');

  // Stepper - progreso del formulario
  const getCurrentStep = () => {
    if (!watchName?.trim()) return 0;
    if (!watchCustomerId) return 1;
    return 2;
  };
  const stepLabels = ['Basico', 'Relaciones', 'Detalles'];

  const filteredProjects = watchCustomerId
    ? projects.filter(p => p.customer_id === watchCustomerId)
    : projects;

  // Filtrar plybooks por cliente/proyecto
  const filteredPlybooks = plybooks.filter(pb => {
    if (watchCustomerId && pb.customer_id && pb.customer_id !== watchCustomerId) return false;
    return true;
  });

  // Agrupar materias primas por su categoría real
  const RAW_CATEGORY_LABELS: Record<string, string> = {
    consumable: 'Consumibles',
    catalyst: 'Catalizadores',
    resin: 'Resinas',
    fiber: 'Fibras',
    core: 'Núcleos',
    adhesive: 'Adhesivos',
    other: 'Otros',
  };
  const groupedMaterials = materials.reduce((groups: Record<string, any[]>, mat) => {
    const category = (mat.category || 'other').trim();
    const groupName = RAW_CATEGORY_LABELS[category.toLowerCase()] || category;

    if (!groups[groupName]) groups[groupName] = [];
    groups[groupName].push(mat);
    return groups;
  }, {});
  // Filtrar materiales por busqueda
  const filteredGroupedMaterials = Object.entries(groupedMaterials).reduce((acc, [group, items]) => {
    const filtered = (items as any[]).filter(m => {
      if (!materialSearch) return true;
      const search = materialSearch.toLowerCase();
      return m.name?.toLowerCase().includes(search) || m.code?.toLowerCase().includes(search);
    });
    if (filtered.length > 0) acc[group] = filtered;
    return acc;
  }, {} as Record<string, any[]>);


  // Add assembly material line
  // Colamination materials helpers
  const addColaminationMaterial = () => {
    setColaminationMaterials([...colaminationMaterials, {
      material_id: '',
      material_name: '',
      quantity: 1,
      unit: 'unidad',
      notes: '',
    }]);
  };

  const removeColaminationMaterial = (index: number) => {
    setColaminationMaterials(colaminationMaterials.filter((_, i) => i !== index));
  };

  const updateColaminationMaterial = (index: number, field: keyof AssemblyMaterial, value: any) => {
    const updated = [...colaminationMaterials];
    if (field === 'material_id' && value) {
      const mat = materials.find((r: any) => r.id === value);
      if (mat) {
        updated[index] = { ...updated[index], material_id: value, material_name: mat.name };
      }
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setColaminationMaterials(updated);
  };

  const addAssemblyMaterial = () => {
    setAssemblyMaterials([...assemblyMaterials, {
      material_id: null,
      material_name: '',
      quantity: 1,
      unit: 'unidad',
      notes: '',
    }]);
  };

  // Remove assembly material line
  const removeAssemblyMaterial = (index: number) => {
    setAssemblyMaterials(assemblyMaterials.filter((_, i) => i !== index));
  };

  // Update assembly material
  const updateAssemblyMaterial = (index: number, field: keyof AssemblyMaterial, value: any) => {
    const updated = [...assemblyMaterials];
    updated[index] = { ...updated[index], [field]: value };

    // If selecting a material from inventory, update name and unit
    if (field === 'material_id' && value) {
      const mat = materials.find(m => m.id === value);
      if (mat) {
        updated[index].material_name = mat.name;
        updated[index].unit = mat.unit || 'unidad';
      }
    }

    setAssemblyMaterials(updated);
  };

  // Expedition materials (mismos handlers que assembly)
  const addExpeditionMaterial = () => {
    setExpeditionMaterials([...expeditionMaterials, {
      material_id: null,
      material_name: '',
      quantity: 1,
      unit: 'unidad',
      notes: '',
    }]);
  };

  const removeExpeditionMaterial = (index: number) => {
    setExpeditionMaterials(expeditionMaterials.filter((_, i) => i !== index));
  };

  const updateExpeditionMaterial = (index: number, field: keyof AssemblyMaterial, value: any) => {
    const updated = [...expeditionMaterials];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'material_id' && value) {
      const mat = materials.find(m => m.id === value);
      if (mat) {
        updated[index].material_name = mat.name;
        updated[index].unit = mat.unit || 'unidad';
      }
    }
    setExpeditionMaterials(updated);
  };

  // Sugerir estantería según pintura/complejidad/nivel si no hay una elegida
  const watchRequiresPaint = form.watch('requires_paint');
  const watchComplexity = form.watch('piece_complexity');
  const watchCutLevel = form.watch('cutting_skill_level');
  useEffect(() => {
    if (!open || shelves.length === 0) return;
    if (form.getValues('cutting_shelf_id')) return;
    let suggested: WorkshopShelf | undefined;
    if (watchRequiresPaint) {
      suggested = shelves.find(s => s.shelf_type === 'paint');
    } else if (watchComplexity === 'composite') {
      suggested = shelves.find(s => s.shelf_type === 'composite');
    } else if (watchCutLevel) {
      suggested = shelves.find(s => s.shelf_type === 'standard' && s.min_skill_level === watchCutLevel);
    }
    if (suggested) form.setValue('cutting_shelf_id', suggested.id);
  }, [watchRequiresPaint, watchComplexity, watchCutLevel, shelves, open]);

  // Add attachment
  const addAttachment = () => {
    setAttachments([...attachments, {
      file_name: '',
      file_url: '',
      file_type: 'pdf',
      version: 'V1',
      description: '',
    }]);
  };

  // Remove attachment
  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  // Update attachment
  const updateAttachment = (index: number, field: keyof Attachment, value: string) => {
    const updated = [...attachments];
    updated[index] = { ...updated[index], [field]: value };
    setAttachments(updated);
  };

  // Upload file for attachment
  const handleFileUpload = async (idx: number, file: File) => {
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/upload?folder=products", { method: "POST", body: formData });
      const json = await res.json();
      if (json.url) {
        updateAttachment(idx, "file_url", json.url);
        if (!attachments[idx].file_name) {
          updateAttachment(idx, "file_name", file.name);
        }
        toast({ title: "Archivo subido", description: file.name });
      }
    } catch (e) {
      toast({ title: "Error", description: "No se pudo subir el archivo", variant: "destructive" });
    } finally {
      setUploadingDoc(false);
    }
  };

  // Create Design/CNC Work Order
  const handleCreateDesignWO = async () => {
    setCreatingWO(true);
    try {
      const formData = form.getValues();

      // Generar order_number primero
      const { data: orderNumber, error: numError } = await api.rpc('generate_work_order_number', {
        p_customer_code: 'GEN',
        p_project_code: 'DIS',
      });
      if (numError) throw numError;

      const { data: wo, error } = await api
        .from('work_orders')
        .insert([{
          order_number: orderNumber,
          order_type: 'design_cnc',
          product_name: `Molde para ${formData.name || 'Nuevo Producto'}`,
          customer_id: formData.customer_id || null,
          project_id: formData.project_id || null,
          status: 'pending',
          planning_status: 'standby',
          notes: `OT de Diseno/CNC generada automaticamente para crear molde del producto`,
          quantity: 1,
        }])
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'OT Creada',
        description: `Se creo la orden de trabajo ${wo.order_number} para diseno del molde`,
      });
      setShowCreateWODialog(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo crear la OT',
        variant: 'destructive',
      });
    } finally {
      setCreatingWO(false);
    }
  };

  const onSubmit = async (data: any) => {
    setLoading(true);
    try {
      const payload = {
        name: data.name,
        reference: data.reference || null,
        version: data.version || 'V1',
        description: data.description || null,
        customer_id: data.customer_id || null,
        project_id: data.project_id || null,
        mold_id: data.mold_id || null,
        plybook_id: data.plybook_id && data.plybook_id !== '__none__' ? data.plybook_id : null,
        document_url: data.document_url || null,
        manual_url: data.manual_url || null,
        client_document_url: data.client_document_url || null,
        client_manual_url: data.client_manual_url || null,
        client_specs_url: data.client_specs_url || null,
        client_portal_url: data.client_portal_url || null,
        finish_type: data.finish_type || null,
        piece_complexity: data.piece_complexity || null,
        requires_paint: !!data.requires_paint,
        cutting_skill_level: data.cutting_skill_level || null,
        cutting_shelf_id: data.cutting_shelf_id || null,
        composite_shelf_id: data.composite_shelf_id || null,
        cutting_max_minutes: data.cutting_max_minutes ? parseInt(data.cutting_max_minutes, 10) : null,
      };

      let productId = product?.id;

      if (product) {
        const { error } = await api
          .from('product_models')
          .update(payload)
          .eq('id', product.id);
        if (error) throw error;
      } else {
        const { data: created, error } = await api
          .from('product_models')
          .insert([payload])
          .select()
          .single();
        if (error) throw error;
        productId = created.id;
      }

      // Save assembly materials
      if (productId) {
        // Delete existing
        await api.from('product_model_assembly_materials').delete().eq('product_model_id', productId);

        // Insert new (el vínculo es a raw_materials; material_id queda como legacy)
        if (assemblyMaterials.length > 0) {
          const materialsToInsert = assemblyMaterials.map((m, idx) => ({
            product_model_id: productId,
            raw_material_id: m.material_id || null,
            material_name: m.material_name || null,
            quantity: m.quantity,
            unit: m.unit,
            notes: m.notes || null,
            sort_order: idx,
          }));
          await api.from('product_model_assembly_materials').insert(materialsToInsert);
        }
        
        // Save colamination materials
        await api.from('product_model_colamination_materials').delete().eq('product_model_id', productId);
        if (colaminationMaterials.length > 0) {
          const colamToInsert = colaminationMaterials.map((m, idx) => ({
            product_model_id: productId,
            raw_material_id: m.material_id || null,
            material_name: m.material_name,
            quantity: m.quantity,
            unit: m.unit,
            notes: m.notes,
            sort_order: idx,
          }));
          await api.from('product_model_colamination_materials').insert(colamToInsert);
        }

        // Save expedition materials
        await api.from('product_model_expedition_materials').delete().eq('product_model_id', productId);
        if (expeditionMaterials.length > 0) {
          const expeditionToInsert = expeditionMaterials.map((m, idx) => ({
            product_model_id: productId,
            raw_material_id: m.material_id || null,
            material_name: m.material_name || null,
            quantity: m.quantity,
            unit: m.unit,
            notes: m.notes || null,
            sort_order: idx,
          }));
          await api.from('product_model_expedition_materials').insert(expeditionToInsert);
        }

        // Save attachments (only new ones without id)
        const newAttachments = attachments.filter(a => !a.id && a.file_url);
        if (newAttachments.length > 0) {
          const attachmentsToInsert = newAttachments.map(a => ({
            product_model_id: productId,
            file_name: a.file_name,
            file_url: a.file_url,
            file_type: a.file_type || null,
            version: a.version || 'V1',
            description: a.description || null,
          }));
          await api.from('product_model_attachments').insert(attachmentsToInsert);
        }
      }

      toast({
        title: product ? 'Producto actualizado' : 'Producto creado',
        description: product ? 'El producto se actualizo correctamente' : 'El producto se creo correctamente',
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar el producto',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[95vw] max-w-[95vw] max-h-[90vh]">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <DialogTitle>{product ? 'Editar Modelo de Producto' : 'Nuevo Modelo de Producto'}</DialogTitle>
              <Badge variant="outline" className="font-mono bg-primary/10">
                PROD-{product ? String(product.product_number).padStart(4, '0') : nextNumber ? String(nextNumber).padStart(4, '0') : '....'}
              </Badge>
            </div>
            {!product && (
              <DialogDescription>
                Centraliza la informacion tecnica, documentacion y materiales del producto
              </DialogDescription>
            )}
          </DialogHeader>

          <CompactStepper steps={stepLabels} currentStep={getCurrentStep()} className="mb-2" />

          <ScrollArea className="max-h-[calc(90vh-180px)] pr-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Informacion Basica</h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      rules={{ required: 'El nombre es obligatorio' }}
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                          <FormLabel>Nombre *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Nombre del producto" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="version"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Version</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="V1" />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="reference"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Referencia / Codigo Cliente</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Ej: 02268-3013" />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Descripcion</FormLabel>
                        <FormControl>
                          <Textarea {...field} placeholder="Descripcion tecnica, observaciones, procesos especiales..." rows={3} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                {/* Relations */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Relaciones</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="customer_id"
                      rules={{ required: 'El cliente es obligatorio' }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cliente *</FormLabel>
                          <FormControl>
                            <SearchableSelect
                              options={customers.map((c) => ({
                                value: c.id,
                                label: c.company_name,
                                description: `C-${String(c.customer_number).padStart(3, '0')}`
                              }))}
                              value={field.value}
                              onValueChange={field.onChange}
                              placeholder="Seleccionar cliente"
                              searchPlaceholder="Buscar cliente..."
                              allowClear={false}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="project_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Proyecto</FormLabel>
                          <FormControl>
                            <SearchableSelect
                              options={filteredProjects.map((p) => ({
                                value: p.id,
                                label: p.name,
                                description: `P-${String(p.project_number).padStart(4, '0')}`
                              }))}
                              value={field.value === '__none__' ? '' : field.value}
                              onValueChange={(v) => field.onChange(v || '__none__')}
                              placeholder="Seleccionar proyecto"
                              searchPlaceholder="Buscar proyecto..."
                              clearText="Sin proyecto"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex items-end gap-2">
                    <FormField
                      control={form.control}
                      name="mold_id"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormLabel>Molde Asociado</FormLabel>
                          <FormControl>
                            <SearchableSelect
                              options={molds.map((m) => ({
                                value: m.id,
                                label: m.name,
                                description: m.code || undefined
                              }))}
                              value={field.value === '__none__' ? '' : field.value}
                              onValueChange={(v) => field.onChange(v || '__none__')}
                              placeholder="Seleccionar molde"
                              searchPlaceholder="Buscar molde..."
                              clearText="Sin molde"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {(!watchMoldId || watchMoldId === '__none__') && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCreateWODialog(true)}
                        className="whitespace-nowrap"
                      >
                        <Wrench className="w-4 h-4 mr-1" />
                        Crear OT Diseno/CNC
                      </Button>
                    )}
                  </div>

                  {/* Plybook para Laminado */}
                  <div className="flex items-end gap-2">
                    <FormField
                      control={form.control}
                      name="plybook_id"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormLabel className="flex items-center gap-2">
                            <BookOpenCheck className="w-4 h-4" />
                            Plybook de Laminado
                          </FormLabel>
                          <FormControl>
                            <SearchableSelect
                              options={filteredPlybooks.map((pb) => ({
                                value: pb.id,
                                label: pb.name,
                                description: pb.code ? pb.code + " - " + (pb.total_plies || 0) + " capas" : (pb.total_plies || 0) + " capas"
                              }))}
                              value={field.value === '__none__' ? '' : field.value}
                              onValueChange={(v) => field.onChange(v || '__none__')}
                              placeholder="Seleccionar plybook"
                              searchPlaceholder="Buscar plybook..."
                              clearText="Sin plybook"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPlybookDialog(true)}
                      className="whitespace-nowrap"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Crear Plybook
                    </Button>

                    {watchPlybookId && watchPlybookId !== '__none__' && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/plybooks/${watchPlybookId}`)}
                        className="whitespace-nowrap"
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Editar Plybook
                      </Button>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Corte Taller y Acabado */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Scissors className="w-4 h-4" />
                    Corte Taller y Acabado
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="finish_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Acabado</FormLabel>
                          <Select value={field.value || '__none__'} onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Sin definir" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="__none__">Sin definir</SelectItem>
                              <SelectItem value="visual">Visual</SelectItem>
                              <SelectItem value="no_visual">No visual</SelectItem>
                              <SelectItem value="na">No aplica</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="piece_complexity"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Complejidad</FormLabel>
                          <Select value={field.value || '__none__'} onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Sin definir" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="__none__">Sin definir</SelectItem>
                              <SelectItem value="simple">Simple</SelectItem>
                              <SelectItem value="composite">Compuesta (pegado de varias piezas)</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="cutting_skill_level"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nivel de corte</FormLabel>
                          <Select value={field.value || '__none__'} onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Sin definir" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="__none__">Sin definir</SelectItem>
                              <SelectItem value="A">A (más difícil)</SelectItem>
                              <SelectItem value="B">B</SelectItem>
                              <SelectItem value="C">C (más fácil)</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <FormField
                      control={form.control}
                      name="requires_paint"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between rounded-lg border p-3">
                          <FormLabel className="text-sm">Va a pintura</FormLabel>
                          <FormControl>
                            <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="cutting_shelf_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Estantería asignada</FormLabel>
                          <Select value={field.value || '__none__'} onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Sin estantería" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="__none__">Sin estantería</SelectItem>
                              {shelves.map((s) => (
                                <SelectItem key={s.id} value={s.id}>
                                  {s.code} - {s.name} (nivel {s.min_skill_level})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="cutting_max_minutes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tiempo máx. corte (min)</FormLabel>
                          <FormControl>
                            <Input {...field} type="number" min={0} placeholder="Ej: 45" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="composite_shelf_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Estantería Compuesta (Nivel A)</FormLabel>
                          <Select value={field.value || '__none__'} onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Sin estantería" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="__none__">Sin estantería</SelectItem>
                              {shelves.filter(s => s.shelf_type === 'composite').map((s) => (
                                <SelectItem key={s.id} value={s.id}>
                                  {s.code} - {s.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <Separator />

                {/* Client URLs - Collapsible */}
                <Collapsible open={urlsOpen} onOpenChange={setUrlsOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                        <Link2 className="w-4 h-4" />
                        URLs del Cliente
                      </h3>
                      <ChevronDown className={`w-4 h-4 transition-transform ${urlsOpen ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="client_document_url"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Documento Cliente</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="https://..." type="url" />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="client_manual_url"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Manual Cliente</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="https://..." type="url" />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="client_specs_url"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Especificacion Tecnica</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="https://..." type="url" />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="client_portal_url"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Portal Cliente</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="https://..." type="url" />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <Separator />

                {/* Documentation - Collapsible */}
                <Collapsible open={docsOpen} onOpenChange={setDocsOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Documentacion Tecnica ({attachments.length})
                      </h3>
                      <ChevronDown className={`w-4 h-4 transition-transform ${docsOpen ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4 space-y-3">
                    {attachments.map((att, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 border rounded-lg bg-muted/30">
                        <Input
                          value={att.file_name}
                          onChange={(e) => updateAttachment(idx, 'file_name', e.target.value)}
                          placeholder="Nombre archivo"
                          className="flex-1"
                        />
                        <div className="flex gap-1 flex-1">
                          <Input
                            value={att.file_url}
                            onChange={(e) => updateAttachment(idx, 'file_url', e.target.value)}
                            placeholder="URL o subir archivo"
                            className="flex-1"
                          />
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.doc,.docx,.xls,.xlsx"
                            ref={(el) => { fileInputRefs.current[idx] = el; }}
                            onChange={(e) => e.target.files?.[0] && handleFileUpload(idx, e.target.files[0])}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => fileInputRefs.current[idx]?.click()}
                            disabled={uploadingDoc}
                            title="Subir archivo"
                          >
                            <Upload className="w-4 h-4" />
                          </Button>
                        </div>
                        <Input
                          value={att.version}
                          onChange={(e) => updateAttachment(idx, 'version', e.target.value)}
                          placeholder="V1"
                          className="w-16"
                        />
                        {att.file_url && (
                          <Button type="button" variant="ghost" size="icon" asChild>
                            <a href={att.file_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </Button>
                        )}
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeAttachment(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addAttachment}>
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar documento
                    </Button>
                  </CollapsibleContent>
                </Collapsible>

                <Separator />

                {/* Colamination Materials - Collapsible */}
                <Collapsible open={colamOpen} onOpenChange={setColamOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                        <Layers className="w-4 h-4" />
                        Materiales de Colaminación ({colaminationMaterials.length})
                      </h3>
                      <ChevronDown className={`w-4 h-4 transition-transform ${colamOpen ? "rotate-180" : ""}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4 space-y-3">
                    {colaminationMaterials.map((mat, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 border rounded-lg bg-muted/30">
                        <Select
                          value={mat.material_id || '__manual__'}
                          onValueChange={(v) => updateColaminationMaterial(idx, 'material_id', v === '__manual__' ? '' : v)}
                        >
                          <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Seleccionar material" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__manual__">Manual</SelectItem>
                            {materials.map((r: any) => (
                              <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          value={mat.material_name}
                          onChange={(e) => updateColaminationMaterial(idx, 'material_name', e.target.value)}
                          placeholder="Nombre"
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          value={mat.quantity}
                          onChange={(e) => updateColaminationMaterial(idx, 'quantity', parseFloat(e.target.value) || 1)}
                          className="w-20"
                          min={0}
                          step={0.01}
                        />
                        <Input
                          value={mat.unit}
                          onChange={(e) => updateColaminationMaterial(idx, 'unit', e.target.value)}
                          placeholder="Unidad"
                          className="w-24"
                        />
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeColaminationMaterial(idx)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addColaminationMaterial} className="w-full">
                      <Plus className="w-4 h-4 mr-2" /> Agregar Material de Colaminación
                    </Button>
                  </CollapsibleContent>
                </Collapsible>

                <Separator />

                {/* Assembly Materials - Collapsible */}
                <Collapsible open={materialsOpen} onOpenChange={setMaterialsOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Materiales de Ensamblaje ({assemblyMaterials.length})
                      </h3>
                      <ChevronDown className={`w-4 h-4 transition-transform ${materialsOpen ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4 space-y-3">
                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar material por nombre o código..."
                        value={materialSearch}
                        onChange={(e) => setMaterialSearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    {assemblyMaterials.map((mat, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 border rounded-lg bg-muted/30">
                        <span className="text-sm text-muted-foreground w-6">{idx + 1}</span>
                        <Select
                          value={mat.material_id || '__manual__'}
                          onValueChange={(v) => updateAssemblyMaterial(idx, 'material_id', v === '__manual__' ? null : v)}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Seleccionar material" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            <SelectItem value="__manual__">Introducir manualmente</SelectItem>
                            {Object.entries(filteredGroupedMaterials).map(([groupName, items]) => (
                              <SelectGroup key={groupName}>
                                <SelectLabel className="text-xs font-semibold text-primary">{groupName}</SelectLabel>
                                {(items as any[]).map((m) => (
                                  <SelectItem key={m.id} value={m.id}>
                                    {m.code ? m.code + " - " : ""}{m.name}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                        {!mat.material_id && (
                          <Input
                            value={mat.material_name}
                            onChange={(e) => updateAssemblyMaterial(idx, 'material_name', e.target.value)}
                            placeholder="Nombre material"
                            className="flex-1"
                          />
                        )}
                        <Input
                          type="number"
                          value={mat.quantity}
                          onChange={(e) => updateAssemblyMaterial(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-20"
                          min={0}
                          step={0.001}
                        />
                        <Select
                          value={mat.unit}
                          onValueChange={(v) => updateAssemblyMaterial(idx, 'unit', v)}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unidad">Unidad</SelectItem>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="g">g</SelectItem>
                            <SelectItem value="m">m</SelectItem>
                            <SelectItem value="m2">m2</SelectItem>
                            <SelectItem value="ml">ml</SelectItem>
                            <SelectItem value="l">L</SelectItem>
                            <SelectItem value="cartucho">Cartucho</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          value={mat.notes}
                          onChange={(e) => updateAssemblyMaterial(idx, 'notes', e.target.value)}
                          placeholder="Notas"
                          className="w-32"
                        />
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeAssemblyMaterial(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addAssemblyMaterial}>
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar material
                    </Button>
                  </CollapsibleContent>
                </Collapsible>

                <Separator />

                {/* Expedition Materials - Collapsible */}
                <Collapsible open={expeditionOpen} onOpenChange={setExpeditionOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                        <Truck className="w-4 h-4" />
                        Materiales de Expedición ({expeditionMaterials.length})
                      </h3>
                      <ChevronDown className={`w-4 h-4 transition-transform ${expeditionOpen ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4 space-y-3">
                    {expeditionMaterials.map((mat, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 border rounded-lg bg-muted/30">
                        <span className="text-sm text-muted-foreground w-6">{idx + 1}</span>
                        <Select
                          value={mat.material_id || '__manual__'}
                          onValueChange={(v) => updateExpeditionMaterial(idx, 'material_id', v === '__manual__' ? null : v)}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Seleccionar material" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            <SelectItem value="__manual__">Introducir manualmente</SelectItem>
                            {Object.entries(filteredGroupedMaterials).map(([groupName, items]) => (
                              <SelectGroup key={groupName}>
                                <SelectLabel className="text-xs font-semibold text-primary">{groupName}</SelectLabel>
                                {(items as any[]).map((m) => (
                                  <SelectItem key={m.id} value={m.id}>
                                    {m.code ? m.code + " - " : ""}{m.name}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                        {!mat.material_id && (
                          <Input
                            value={mat.material_name}
                            onChange={(e) => updateExpeditionMaterial(idx, 'material_name', e.target.value)}
                            placeholder="Nombre material"
                            className="flex-1"
                          />
                        )}
                        <Input
                          type="number"
                          value={mat.quantity}
                          onChange={(e) => updateExpeditionMaterial(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-20"
                          min={0}
                          step={0.001}
                        />
                        <Select
                          value={mat.unit}
                          onValueChange={(v) => updateExpeditionMaterial(idx, 'unit', v)}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unidad">Unidad</SelectItem>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="g">g</SelectItem>
                            <SelectItem value="m">m</SelectItem>
                            <SelectItem value="m2">m2</SelectItem>
                            <SelectItem value="ml">ml</SelectItem>
                            <SelectItem value="l">L</SelectItem>
                            <SelectItem value="cartucho">Cartucho</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          value={mat.notes}
                          onChange={(e) => updateExpeditionMaterial(idx, 'notes', e.target.value)}
                          placeholder="Notas"
                          className="w-32"
                        />
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeExpeditionMaterial(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addExpeditionMaterial}>
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar material de expedición
                    </Button>
                  </CollapsibleContent>
                </Collapsible>
              </form>
            </Form>
          </ScrollArea>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={form.handleSubmit(onSubmit)} disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {product ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Design WO Confirmation */}
      <AlertDialog open={showCreateWODialog} onOpenChange={setShowCreateWODialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Crear Orden de Trabajo de Diseno/CNC</AlertDialogTitle>
            <AlertDialogDescription>
              Se creara una Orden de Trabajo de tipo Diseno/CNC para fabricar el molde de este modelo de producto.
              La OT incluira automaticamente el cliente y proyecto seleccionados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={creatingWO}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleCreateDesignWO} disabled={creatingWO}>
              {creatingWO && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Crear OT
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Plybook Dialog */}
      <PlybookFormDialog
        open={showPlybookDialog}
        onOpenChange={setShowPlybookDialog}
        defaultCustomerId={watchCustomerId || undefined}
        defaultProjectId={form.watch('project_id') || undefined}
        defaultMoldId={watchMoldId && watchMoldId !== '__none__' ? watchMoldId : undefined}
        defaultName={form.watch('name') || undefined}
        defaultCode={form.watch('reference') || undefined}
        defaultVersion={form.watch('version') || undefined}
        hideProductField={true}
        onSuccess={(created) => {
          fetchPlybooks();
          // Autoseleccionar el plybook recién creado en el producto
          if (created?.id) form.setValue('plybook_id', created.id);
          setShowPlybookDialog(false);
        }}
      />
    </>
  );
}
